import { useEffect, useState } from "react";
import {
  FiX,
  FiCopy,
  FiDownload,
  FiPrinter,
  FiRefreshCw,
  FiKey,
  FiAlertTriangle,
} from "react-icons/fi";
import { Spinner } from "components/ui";
import { BackupCodeGrid, MfaIconBadge, mfaActionButtonClass } from "./MfaUi";
import { toast } from "react-toastify";

import { useMutation } from "@tanstack/react-query";
import { authService } from "./mfa.api";
import { useAuth } from "context/auth-context";

type Props = {
  onClose: () => void;
};

const BackupCodesModal = ({ onClose }: Props) => {
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const { auth } = useAuth();

  // 🔥 fetch codes
  const { mutateAsync: fetchCodes, isLoading: loadingFetch } = useMutation({
    mutationFn: authService.getBackupCodes,
    onSuccess: (res: any) => {
      setBackupCodes(res?.data?.data?.backupCodes || []);
    },
    onError: () => {
      toast.error("Failed to load backup codes");
    },
  });

  // 🔥 regenerate
  const { mutateAsync: regenerateCodes, isLoading: loadingRegen } = useMutation({
    mutationFn: authService.regenerateBackupCodes,
    onSuccess: (res: any) => {
      setBackupCodes(res?.data?.data?.backupCodes || []);
      toast.success("Backup codes regenerated");
    },
    onError: () => {
      toast.error("Failed to regenerate");
    },
  });

  const loading = loadingFetch || loadingRegen;

  useEffect(() => {
    fetchCodes(auth?.email);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Copied to clipboard");
  };

  const handleDownload = () => {
    const blob = new Blob([backupCodes.join("\n")], {
      type: "text/plain",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "mfa-backup-codes.txt";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const win = window.open("", "", "width=600,height=600");

    win?.document.write(`
      <div style="font-family: monospace; padding:20px">
        <h3>Backup Codes</h3>
        <pre>${backupCodes.join("\n")}</pre>
      </div>
    `);

    win?.document.close();
    win?.print();
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Old codes will stop working. Continue?")) return;
    await regenerateCodes(auth?.email);
  };

  return (
    <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="mfa-backup-title">
      <div className="zs-modal max-w-md">
        {/* Close */}
        <button onClick={onClose} className="zs-icon-btn zs-modal-close" aria-label="Close">
          <FiX size={18} />
        </button>

        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-4 pr-14 border-b sm:px-6 border-line">
          <MfaIconBadge>
            <FiKey />
          </MfaIconBadge>
          <div className="min-w-0">
            <h2 id="mfa-backup-title" className="text-base font-semibold font-display text-ink">
              Save your backup codes
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              Store these codes safely. You can use them if you lose access to your authenticator.
            </p>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm text-ink-3">
              <Spinner className="w-6 h-6" />
              loading...
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Codes */}
              <BackupCodeGrid codes={backupCodes} />

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={handleCopy} className={mfaActionButtonClass}>
                  <FiCopy size={14} />
                  Copy
                </button>

                <button onClick={handleDownload} className={mfaActionButtonClass}>
                  <FiDownload size={14} />
                  Download
                </button>

                <button onClick={handlePrint} className={mfaActionButtonClass}>
                  <FiPrinter size={14} />
                  Print
                </button>

                <button onClick={handleRegenerate} className={`${mfaActionButtonClass} text-danger`}>
                  <FiRefreshCw size={14} />
                  Regenerate
                </button>
              </div>

              {/* Footer */}
              <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-warning">
                <FiAlertTriangle className="w-3.5 h-3.5" />
                Each backup code can be used only once
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupCodesModal;
